import { Component, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { DEFAULT_ELCI_LANGUAGE } from '@app/core/constants/Constants';
import CountryEnum from '@app/core/enums/country.enum';
import PaymentMethodTypeEnum from '@app/core/enums/payment-method-type.enum';
import RegistryTypeEnum from '@app/core/enums/registry-type.enum';
import {
  BillingType,
  CompanyParameters,
  DoctorsAmounts,
  PaymentMethodTypes,
  RegistryTypes,
  SignaturesAmounts,
  SubscriptionTypes,
} from '@app/core/models/input/company/company-parameters.model';
import { Country } from '@app/core/models/input/country.model';
import { Specialty } from '@app/core/models/input/specialty.model';
import { State } from '@app/core/models/input/state.model';
import { AlertService } from '@app/core/services/alert/alert.service';
import { SpecialtiesService } from '@app/core/services/api/consents/specialties/specialties.service';
import { CompanyService } from '@app/core/services/company/company.service';
import { CountryService } from '@app/core/services/country/country.service';
import { DataClientService } from '@app/core/services/data-client/data-client.service';
import { StateService } from '@app/core/services/state/state.service';
import { ElciValidators } from '@app/core/utils/validators';
import { TranslateCustomService } from '@app/shared/services/translate/translate.service';
import { faCircleArrowRight, faCircleXmark } from '@fortawesome/free-solid-svg-icons';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-company-hiring',
  templateUrl: './company-hiring.component.html',
  styleUrls: ['./company-hiring.component.scss'],
  standalone: false,
})
export class CompanyHiringComponent implements OnInit {
  private readonly dataClientService = inject(DataClientService);
  private readonly alertService = inject(AlertService);
  private readonly translate = inject(TranslateService);
  private readonly companyService = inject(CompanyService);
  private readonly specialtiesService = inject(SpecialtiesService);
  private readonly countryService = inject(CountryService);
  private readonly translateCustom = inject(TranslateCustomService);
  private readonly stateService = inject(StateService);

  faCircleXmark = faCircleXmark;
  faCircleArrowRight = faCircleArrowRight;

  dataHiring!: FormGroup;
  dataCompany!: FormGroup;
  dataAccess!: FormGroup;
  dataContact!: FormGroup;
  dataRgpd!: FormGroup;

  classInput = '';
  isSubmit = false;
  isSepa = false;
  isHis = false;
  isCheckedCopied = false;
  dataConfirm = false;

  parameters?: CompanyParameters[];
  billingTypes?: BillingType[];
  doctorsAmounts?: DoctorsAmounts[];
  paymentMethodTypes?: PaymentMethodTypes[];
  registryTypes?: RegistryTypes[];
  signaturesAmounts?: SignaturesAmounts[];
  subscriptionTypes?: SubscriptionTypes[];
  specialty?: Specialty[] = [];
  specialties?: Specialty[];
  countries?: Country[];
  countryId = 1;
  states?: State[];
  selectedLanguage?: Country;

  // función que valida cada dato del formulario
  shouldShowError(form: FormGroup, controlName: string): boolean {
    const control = form.get(controlName);
    return !!(control && control.touched && control.invalid);
  }

  ngOnInit(): void {
    // Datos de contratación
    this.dataHiring = new FormGroup({
      doctorsAmountId: new FormControl({ value: '', disabled: false }, [Validators.required]),

      billingTypeId: new FormControl({ value: '', disabled: false }, [Validators.required]),
      paymentMethodTypeId: new FormControl({ value: '', disabled: false }, [Validators.required]),
      registryTypeId: new FormControl({ value: '', disabled: false }, [Validators.required]),
      price: new FormControl({ value: '', disabled: false }, [
        Validators.required,
        Validators.pattern(/^\d+(\.\d{1,2})?$/),
      ]),
      subscriptionsTypeId: new FormControl({ value: '', disabled: false }, [Validators.required]),
      signaturesAmountId: new FormControl({ value: '', disabled: false }, [Validators.required]),
      his: new FormControl({ value: '', disabled: false }, [ElciValidators.fieldValidator]),
      specialtiesId: new FormControl({ value: null, disabled: false }, [Validators.required]),
      specialtiesName: new FormControl({ value: '', disabled: false }),
      bankAccount: new FormControl({ value: '', disabled: false }, [ElciValidators.documentValidator]),
    });

    // Datos de la empresa
    this.dataCompany = new FormGroup({
      businessName: new FormControl({ value: '', disabled: false }, [
        Validators.required,
        ElciValidators.fieldValidator,
      ]),
      website: new FormControl({ value: '', disabled: false }, [ElciValidators.fieldValidator]),
      name: new FormControl({ value: '', disabled: false }, [ElciValidators.nameValidator]),
      email: new FormControl({ value: '', disabled: false }, [ElciValidators.emailValidator]),
      cif: new FormControl({ value: '', disabled: false }, [Validators.required, ElciValidators.documentValidator]),
      dniCif: new FormControl({ value: '', disabled: false }, [ElciValidators.documentValidator]),
      mobile: new FormControl({ value: '', disabled: false }, [ElciValidators.phoneValidator]),
    });

    // Datos de acceso
    this.dataAccess = new FormGroup(
      {
        username: new FormControl({ value: '', disabled: false }, [
          Validators.required,
          ElciValidators.documentValidator,
          Validators.minLength(8),
        ]),
        password: new FormControl({ value: '', disabled: false }, [
          Validators.required,
          ElciValidators.passwordValidator,
        ]),
        repeatPass: new FormControl({ value: '', disabled: false }, [
          Validators.required,
          ElciValidators.passwordValidator,
        ]),
        countryId: new FormControl({ value: '', disabled: false }, [Validators.required]),
      },
      [ElciValidators.passwordMatchValidator]
    );

    // Datos de contacto
    this.dataContact = new FormGroup({
      address: new FormControl({ value: '', disabled: false }, [Validators.required, ElciValidators.addressValidator]),
      postCode: new FormControl({ value: '', disabled: false }, [
        Validators.required,
        ElciValidators.postcodeValidator,
      ]),

      email: new FormControl({ value: '', disabled: false }, [Validators.required, ElciValidators.emailValidator]),
      phone: new FormControl({ value: '', disabled: false }, [ElciValidators.phoneValidator]),
      locality: new FormControl({ value: '', disabled: false }, [Validators.required, ElciValidators.fieldValidator]),
      stateId: new FormControl({ value: '', disabled: false }, [Validators.required]),
      mobile: new FormControl({ value: '', disabled: false }, [Validators.required, ElciValidators.phoneValidator]),
    });

    // Datos de RGPD
    this.dataRgpd = new FormGroup({
      copied: new FormControl({ value: false, disabled: false }),
      businessNameFis: new FormControl({ value: '', disabled: false }, [
        Validators.required,
        ElciValidators.fieldValidator,
      ]),
      addressFis: new FormControl({ value: '', disabled: false }, [
        Validators.required,
        ElciValidators.addressValidator,
      ]),
      postCodeFis: new FormControl({ value: '', disabled: false }, [Validators.required,ElciValidators.postcodeValidator]),
      emailLOPD: new FormControl({ value: '', disabled: false }, [Validators.required,ElciValidators.emailValidator]),
      cifFis: new FormControl({ value: '', disabled: false }, [Validators.required, ElciValidators.documentValidator]),
      localityFis: new FormControl({ value: '', disabled: false }, [
        Validators.required,
        ElciValidators.fieldValidator,
      ]),
      stateIdFis: new FormControl({ value: '', disabled: false }, [Validators.required]),
    });

    this.companyService.getParametersToCreate().subscribe({
      next: data => {
        this.billingTypes = data['billingTypes'] as BillingType[];
        this.doctorsAmounts = data['doctorsAmounts'] as DoctorsAmounts[];
        this.paymentMethodTypes = data['paymentMethodTypes'] as PaymentMethodTypes[];
        this.registryTypes = data['registryTypes'] as RegistryTypes[];
        this.signaturesAmounts = data['signaturesAmounts'] as SignaturesAmounts[];
        this.subscriptionTypes = data['subscriptionTypes'] as SubscriptionTypes[];
      },
    });

    // consultamos las especialidades
    this.specialtiesService.getSpecialties().subscribe({
      next: specialties => {
        this.specialties = specialties;
      },
    });

    this.getCountries();

    // carga las provincias y el validador de documento en función del país elegido
    this.dataAccess.get('countryId')?.valueChanges.subscribe(countryId => {
      const country = this.countries?.find(c => c.id === countryId);
      if (countryId && country) {
        this.getProvinces(countryId);
        this.changeLanguageByCountry(country);
        if (countryId === CountryEnum.ECUADOR) {
          this.dataCompany.get('cif')?.setValidators([Validators.pattern(/^\d{13}$/)]);
        } else {
          this.dataCompany.get('cif')?.setValidators([Validators.pattern(/^\w{9}$/)]);
        }
        this.dataCompany.get('cif')?.updateValueAndValidity();
      }
    });

    // para autorellenar los datos RGPD al marcar el check
    this.dataRgpd.get('copied')?.valueChanges.subscribe((copied: boolean) => {
      if (copied) {
        this.fillInputs();
      } else {
        this.clearRgpdInputs();
      }
    });
  }

  checkSepa() {
    if (this.dataHiring.get('paymentMethodTypeId')?.value == PaymentMethodTypeEnum.SEPA) {
      this.isSepa = true;
      this.dataHiring.get('bankAccount')?.setValidators([Validators.required]);
    } else {
      this.isSepa = false;
      this.dataHiring.get('bankAccount')?.clearValidators();
    }
    this.dataHiring.get('bankAccount')?.updateValueAndValidity();
  }

  checkHis() {
    if (this.dataHiring.get('registryTypeId')?.value == RegistryTypeEnum.HIS) {
      this.isHis = true;
      this.dataHiring.get('his')?.setValidators([Validators.required]);
      this.dataHiring.get('signaturesAmountId')?.clearValidators();
    } else {
      this.isHis = false;
      this.dataHiring.get('his')?.clearValidators();
      this.dataHiring.get('signaturesAmountId')?.setValidators([Validators.required]);
    }

    this.dataHiring.get('his')?.updateValueAndValidity();
    this.dataHiring.get('signaturesAmountId')?.updateValueAndValidity();
  }

  // para quitar una especialidad
  deleteItem(i: number, specialty: Specialty) {
    this.specialty?.splice(i, 1);
    this.specialties?.push(specialty);
    this.specialties = this.specialties?.sort((a, b) => {
      const descriptionA = a.name || '';
      const descriptionB = b.name || '';
      return descriptionA.localeCompare(descriptionB);
    });
    this.updateSpecialtiesForm();
  }
  // para añadir una especialidad
  addItem(i: number, specialtyAdd: Specialty) {
    if (this.specialty != undefined) {
      this.specialties?.splice(i, 1);
      this.specialty?.push(specialtyAdd);
    }
    this.updateSpecialtiesForm();
  }

  updateSpecialtiesForm() {
    // Guardamos los IDs de las especialidades seleccionadas
    const ids = this.specialty?.map(s => s.id) ?? [];
    this.dataHiring.controls['specialtiesId'].setValue(ids.length > 0 ? ids : null);

    // Guardamos los nombres de las especialidades seleccionadas
    const names = this.specialty?.map(s => s.name) ?? [];
    this.dataHiring.controls['specialtiesName'].setValue(names.join(', '));
  }

  editField() {
    const error = document.querySelector('.messageSpecialty');
    const box = document.querySelector('.specialty_select');
    error?.classList.remove('wrongtext');
    box?.classList.remove('wrong');
  }

  getCountries(): void {
    this.countryService.getCountries().subscribe({
      next: countries => {
        this.countries = countries;       
      },
    });
  }

 changeLanguageByCountry(country: Country) {
  const locale = country.defaultLanguage?.locale?.replace('_', '-') ?? DEFAULT_ELCI_LANGUAGE;
  this.translateCustom.changeLang(locale);
} 

  getProvinces(countryId: number): void {
    this.stateService.getStateByCountryId(countryId).subscribe({
      next: states => {
        this.states = states;
      },
    });
  }

  clearRgpdInputs(): void {
    this.dataRgpd.patchValue({
      businessNameFis: '',
      cifFis: '',
      addressFis: '',
      postCodeFis: '',
      emailLOPD: '',
      localityFis: '',
      stateIdFis: '',
    });
  }

  fillInputs(): void {
    this.dataRgpd.patchValue({
      businessNameFis: this.dataCompany.get('businessName')?.value,
      cifFis: this.dataCompany.get('cif')?.value,
      addressFis: this.dataContact.get('address')?.value,
      postCodeFis: this.dataContact.get('postCode')?.value,
      emailLOPD: this.dataContact.get('email')?.value,
      localityFis: this.dataContact.get('locality')?.value,
      stateIdFis: this.dataContact.get('stateId')?.value,
    });
  }

  // revisa cada campo de cada formulario
  markFormGroupTouched(form: FormGroup) {
    Object.values(form.controls).forEach(control => {
      control.markAsTouched();
      if ((control as FormGroup).controls) {
        this.markFormGroupTouched(control as FormGroup);
      }
    });
  }

  onSubmit() {
    this.isSubmit = true;
    this.markFormGroupTouched(this.dataHiring);
    this.markFormGroupTouched(this.dataCompany);
    this.markFormGroupTouched(this.dataAccess);
    this.markFormGroupTouched(this.dataContact);
    this.markFormGroupTouched(this.dataRgpd);

    if (
      this.dataHiring.valid &&
      this.dataCompany.valid &&
      this.dataAccess.valid &&
      this.dataContact.valid &&
      this.dataRgpd.valid
    ) {
      // Guardamos los formularios en el service
      this.dataClientService.addFormGroup('dataHiring', this.dataHiring);
      this.dataClientService.addFormGroup('dataCompany', this.dataCompany);
      this.dataClientService.addFormGroup('dataAccess', this.dataAccess);
      this.dataClientService.addFormGroup('dataContact', this.dataContact);
      this.dataClientService.addFormGroup('dataRgpd', this.dataRgpd);
      this.dataConfirm = true;
      window.scrollTo(0, 0);
    } else {
      this.classInput = 'wrong';
      this.alertService.error(this.translate.instant('PRIVATE.PATIENT.DATA-PATIENT.MESSAGE-PATIENT-NEW-ERROR'));
    }
  }
}
